/**
 * Input Area field to provide multi line Input
 *
 * @module views/components/InputArea
 * @memberof -Common
 */
import './InputArea.scss';

import React, { useCallback, useRef } from 'react';

import { useField } from 'formik';
import PropTypes from 'prop-types';
import { InputArea as PalInputArea } from 'web-palette/dist/components/InputArea/InputArea';

import { spreadProps } from '@ulta/core/utils/dom/dom';

/**
 * Represents a InputArea component
 *
 * @method
 * @param {InputAreaProps} props - React properties passed from composition
 * @returns InputArea
 */
export const InputArea = function( props ){
  const {
    id,
    name,
    onBlur,
    onChange,
    ariaId,
    instructionalText,
    message,
    messageType,
    autoCorrect = defaultProps.autoCorrect,
    spellCheck = defaultProps.spellCheck,
    ...rest
  } = props;
  const inputRef = useRef( null );
  const [field] = useField( props );

  const handleChange = useCallback( ( event ) => {
    field.onChange( event );
    if( onChange ){
      onChange( event.target.value );
    }
  }, [field, onChange] );

  const handleBlur = useCallback( ( event ) => {
    field.onBlur( event );
    if( onBlur ){
      onBlur( event );
    }
  }, [field, onBlur] );

  return (
    <div className='InputArea'>
      <PalInputArea
        inputRef={ inputRef }
        { ...spreadProps( { ...rest } ) }
        id={ id || field.name }
        name={ field.name }
        value={ field.value || '' }
        helperText={ instructionalText }
        message={ message }
        messageType={ messageType }
        ariaDescribedBy={ ariaId }
        autoCorrect={ autoCorrect }
        spellCheck={ spellCheck }
        onChange={ handleChange }
        onBlur={ handleBlur }
      />
    </div>
  );
};

/**
 * Property type definitions
 * @typedef InputAreaProps
 * @type {object}
 * @property {string} id - The input field id attribute value
 * @property {string} name - The input field name attribute value
 * @property {string} label - Label of the input to render over the inputbox
 * @property {number} maxLength - Sets the maximum length attribute value for the input field
 * @property {boolean} disabled - Flag to disable input field
 * @property {string} placeholder - The input field placeholder attribute value
 * @property {string} ariaLabel - The input field aria-label attribute value
 * @property {string} ariaId - The input field's aria-describedby ID
 * @property {Function} onFocus - Function to handle focus event
 * @property {string} instructionalText - Instructional text displayed below the input field
 * @property {string} message - Error or info message to display
 * @property {string} messageType - Type of message (e.g., 'error', 'info')
 * @property {string} className - Class name for the component
 * @property {string} inputClassName - Class name for the input element
 * @property {Function} onPaste - Function to handle paste event
 * @property {Function} onBlur - Function to handle blur event
 * @property {Function} onClick - Function to handle click event
 * @property {Function} onChange - Function to handle change event
 * @property {number} rows - Number of rows for the textarea
 * @property {number} tabIndex - The input field tabIndex attribute value
 * @property {boolean} readOnly - Flag to make the input field read-only
 * @property {boolean} required - Flag to make the input field required
 * @property {string} autoComplete - The input field autocomplete attribute value
 * @property {string} autoCapitalize - The input field autoCapitalize attribute value
 * @property {string} autoCorrect - The input field autoCorrect attribute value
 * @property {boolean} spellCheck - The input field spellCheck attribute value
 */
export const propTypes = {
  /** The input field id attribute value */
  id: PropTypes.string,
  /** The input field name attribute value */
  name: PropTypes.string,
  /** Label of the input to render over the inputbox */
  label: PropTypes.string,
  /** Sets the maximum length attribute value for the input field */
  maxLength: PropTypes.number,
  /** Flag to disable input field */
  disabled: PropTypes.bool,
  /** The input field placeholder attribute value */
  placeholder: PropTypes.string,
  /** The input field aria-label attribute value */
  ariaLabel: PropTypes.string,
  /** The input field's aria-describedby ID */
  ariaId: PropTypes.string,
  /** Function to handle focus event */
  onFocus: PropTypes.func,
  /** Instructional text displayed below the input field */
  instructionalText: PropTypes.string,
  /** Error or info message to display */
  message: PropTypes.string,
  /** Type of message (e.g., 'error', 'info') */
  messageType: PropTypes.string,
  /** Class name for the component */
  className: PropTypes.string,
  /** Class name for the input element */
  inputClassName: PropTypes.string,
  /** Function to handle paste event */
  onPaste: PropTypes.func,
  /** Function to handle blur event */
  onBlur: PropTypes.func,
  /** Function to handle click event */
  onClick: PropTypes.func,
  /** Function to handle change event */
  onChange: PropTypes.func,
  /** Number of rows for the textarea */
  rows: PropTypes.number,
  /** The input field tabIndex attribute value */
  tabIndex: PropTypes.number,
  /** Flag to make the input field read-only */
  readOnly: PropTypes.bool,
  /** Flag to make the input field required */
  required: PropTypes.bool,
  /** The input field autocomplete attribute value */
  autoComplete: PropTypes.string,
  /** The input field autoCapitalize attribute value */
  autoCapitalize: PropTypes.string,
  /** The input field autoCorrect attribute value */
  autoCorrect: PropTypes.string,
  /** The input field spellCheck attribute value */
  spellCheck: PropTypes.bool
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} autoCorrect='on' - The input field autocorrect attribute, default is 'on'
 * @property {boolean} spellCheck=true - Flag to trigger spellcheck in the field, default is 'true'
 */
export const defaultProps = {
  autoCorrect: 'on',
  spellCheck: true
};

InputArea.propTypes = propTypes;
InputArea.defaultProps = defaultProps;

export default InputArea;
